import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Download, Mail, Video } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function BarList({ title, data, testid }) {
  const entries = Object.entries(data);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div data-testid={testid} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <p className="font-display text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70">{title}</p>
      <div className="mt-5 space-y-3">
        {entries.length === 0 && <p className="text-sm text-white/50">No data yet</p>}
        {entries.map(([label, value]) => (
          <div key={label}>
            <div className="flex items-center justify-between gap-4 text-[13px]">
              <span className="min-w-0 truncate text-white/70">{label}</span>
              <span className="shrink-0 text-white/70">{value}</span>
            </div>
            <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/[0.06]">
              <div className="h-full rounded-full bg-cyan-300/80" style={{ width: `${(value / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DailyChart({ data }) {
  const entries = Object.entries(data);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div data-testid="stats-daily" className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <p className="font-display text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70">
        Traffic over time — daily page views
      </p>
      {entries.length === 0 ? (
        <p className="mt-5 text-sm text-white/50">No data yet</p>
      ) : (
        <div className="mt-6 flex h-36 items-end gap-1.5">
          {entries.map(([day, value]) => (
            <div key={day} className="group flex h-full flex-1 flex-col items-center justify-end">
              <div
                className="w-full rounded-t bg-cyan-300/70 transition-colors group-hover:bg-cyan-200"
                style={{ height: `${Math.max(3, (value / max) * 100)}%` }}
                title={`${day}: ${value} views`}
              />
              <span className="mt-2 hidden text-[9px] text-white/50 sm:block">{day.slice(5)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const FORM_GROUPS = [
  {
    key: "investment_interest",
    title: "Investment expressions of interest",
    testid: "forms-investment",
    replySubject: () => "Re: Your investment expression of interest — AIROBOTCAMSTUDIO",
    fields: [
      ["Email", "email"],
      ["Phone", "phone"],
      ["Company", "company"],
      ["Country", "country"],
      ["Intended amount", "amount"],
      ["Message", "message"],
    ],
  },
  {
    key: "investor_inquiries",
    title: "Investor inquiries",
    testid: "forms-investor",
    replySubject: () => "Re: Your investor inquiry — AIROBOTCAMSTUDIO",
    fields: [
      ["Email", "email"],
      ["Investor type", "investor_type"],
      ["Message", "message"],
    ],
  },
  {
    key: "contact_messages",
    title: "Contact messages",
    testid: "forms-contact",
    replySubject: (item) => `Re: ${item.subject}`,
    fields: [
      ["Email", "email"],
      ["Subject", "subject"],
      ["Message", "message"],
    ],
  },
  {
    key: "beta_interest",
    title: "Beta interest signups",
    testid: "forms-beta",
    replySubject: () => "Re: Your beta interest signup — AIROBOTCAMSTUDIO",
    fields: [
      ["Email", "email"],
      ["Country", "country"],
      ["Message", "message"],
    ],
  },
  {
    key: "early_access",
    title: "Early access registrations",
    testid: "forms-early-access",
    replySubject: () => "Re: Your early access registration — AIROBOTCAMSTUDIO",
    fields: [
      ["Email", "email"],
      ["Display name", "display_name"],
      ["Marketing opt-in", "marketing"],
      ["Aria approved", "aria_approved"],
    ],
  },
];

function csvCell(value) {
  let s = String(value ?? "");
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""').replace(/[\r\n]+/g, " ")}"`;
}

function exportSubmissionsCsv(forms) {
  const keys = ["form_type", "created_at", "name", "email", "subject", "investor_type", "phone", "company", "country", "amount", "display_name", "marketing", "message"];
  const all = FORM_GROUPS.flatMap((group) =>
    (forms[group.key] || []).map((item) => ({ ...item, form_type: group.title }))
  ).sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
  const rows = [keys.map(csvCell).join(",")];
  for (const item of all) {
    rows.push(keys.map((k) => csvCell(item[k])).join(","));
  }
  const blob = new Blob(["\ufeff" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `airobotcamstudio-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function FormFeed({ forms, newSince, dashKey, onChanged }) {
  const [ariaBusy, setAriaBusy] = useState("");

  const toggleAria = async (item) => {
    setAriaBusy(item.id);
    try {
      await axios.post(
        `${API}/aria/approve`,
        { email: item.email, approved: !item.aria_approved },
        { params: { key: dashKey } }
      );
      await onChanged();
    } catch {
      // leave UI unchanged on failure
    } finally {
      setAriaBusy("");
    }
  };

  const isNew = (item) => Boolean(newSince) && Date.parse(item.created_at) > Date.parse(newSince);
  const newCount = FORM_GROUPS.reduce(
    (sum, group) => sum + (forms[group.key] || []).filter(isNew).length,
    0
  );
  return (
    <section data-testid="stats-forms-section" className="mt-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-display text-xl font-light tracking-tighter">Form submissions</h2>
          <span data-testid="forms-total" className="text-[11px] uppercase tracking-[0.25em] text-white/55">
            {forms.total} total
          </span>
          {newCount > 0 && (
            <span
              data-testid="forms-new-count"
              className="rounded-full bg-cyan-300 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.15em] text-black"
            >
              {newCount} new
            </span>
          )}
        </div>
        <button
          data-testid="forms-export"
          onClick={() => exportSubmissionsCsv(forms)}
          disabled={!forms.total}
          className="inline-flex items-center gap-2 rounded-md border border-cyan-300/25 px-4 py-2 text-xs font-semibold text-cyan-200/80 transition-colors hover:border-cyan-300/50 hover:text-cyan-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Download size={13} strokeWidth={2.2} />
          Export CSV
        </button>
      </div>
      <div className="mt-5 space-y-4">
        {FORM_GROUPS.map((group) => {
          const items = forms[group.key] || [];
          return (
            <div
              key={group.key}
              data-testid={group.testid}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-6"
            >
              <div className="flex items-center justify-between gap-4">
                <p className="font-display text-[11px] font-semibold uppercase tracking-[0.25em] text-white/70">
                  {group.title}
                </p>
                <span className="shrink-0 rounded-full border border-cyan-300/30 px-2.5 py-0.5 text-[11px] font-semibold text-cyan-200">
                  {items.length}
                </span>
              </div>
              <div className="mt-4 space-y-3">
                {items.length === 0 && <p className="text-sm text-white/50">No submissions yet</p>}
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={`min-w-0 rounded-xl border p-4 ${
                      isNew(item)
                        ? "border-cyan-300/40 bg-cyan-300/[0.04]"
                        : "border-white/[0.07] bg-black/30"
                    }`}
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <p className="flex min-w-0 flex-wrap items-center gap-2 text-sm font-semibold text-white">
                        <span className="min-w-0 break-words">{item.name}</span>
                        {isNew(item) && (
                          <span
                            data-testid={`new-badge-${item.id}`}
                            className="shrink-0 rounded-full bg-cyan-300 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em] text-black"
                          >
                            New
                          </span>
                        )}
                      </p>
                      <p className="shrink-0 text-[11px] text-white/55">
                        {item.created_at.slice(0, 16).replace("T", " ")} UTC
                      </p>
                    </div>
                    <dl className="mt-2.5 space-y-1.5">
                      {group.fields.map(([label, field]) =>
                        item[field] || item[field] === true ? (
                          <div key={field} className="flex min-w-0 gap-3 text-[13px]">
                            <dt className="w-32 shrink-0 text-white/55">{label}</dt>
                            <dd className="min-w-0 flex-1 break-words text-white/70">
                              {typeof item[field] === "boolean" ? (item[field] ? "Yes" : "No") : item[field]}
                            </dd>
                          </div>
                        ) : null
                      )}
                    </dl>
                    <div className="mt-3 flex flex-wrap items-center gap-4">
                      <a
                        data-testid={`reply-${item.id}`}
                        href={`mailto:${item.email}?subject=${encodeURIComponent(group.replySubject(item))}`}
                        className="inline-flex items-center gap-2 text-xs font-semibold text-cyan-200/90 transition-colors hover:text-cyan-100"
                      >
                        <Mail size={13} strokeWidth={2.2} />
                        Reply by email
                      </a>
                      {group.key === "early_access" && (
                        <button
                          data-testid={`aria-approve-${item.id}`}
                          onClick={() => toggleAria(item)}
                          disabled={ariaBusy === item.id}
                          className={`inline-flex items-center gap-2 text-xs font-semibold transition-colors disabled:opacity-50 ${
                            item.aria_approved
                              ? "text-amber-200/90 hover:text-amber-100"
                              : "text-cyan-200/90 hover:text-cyan-100"
                          }`}
                        >
                          <Video size={13} strokeWidth={2.2} />
                          {ariaBusy === item.id
                            ? "Updating…"
                            : item.aria_approved
                              ? "Revoke Aria access"
                              : "Approve for Aria"}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default function StatsDashboard() {
  const [key, setKey] = useState("");
  const [stats, setStats] = useState(null);
  const [forms, setForms] = useState(null);
  const [newSince, setNewSince] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const visitMarked = useRef(false);

  const refreshForms = async () => {
    try {
      const formsRes = await axios.get(`${API}/analytics/forms`, { params: { key } });
      setForms(formsRes.data);
    } catch {}
  };

  const [ariaKeyInput, setAriaKeyInput] = useState("");
  const [ariaKeySaving, setAriaKeySaving] = useState(false);
  const [ariaKeyMsg, setAriaKeyMsg] = useState("");

  const saveAriaKey = async () => {
    setAriaKeySaving(true);
    setAriaKeyMsg("");
    try {
      await axios.post(
        `${API}/config/lemonslice-key`,
        { key_value: ariaKeyInput.trim() },
        { params: { key } }
      );
      setAriaKeyInput("");
      setAriaKeyMsg("Key saved securely. Aria preview is now connected.");
      const summaryRes = await axios.get(`${API}/analytics/summary`, { params: { key } });
      setStats(summaryRes.data);
    } catch (err) {
      setAriaKeyMsg(err?.response?.data?.detail || "Could not save the key. Please try again.");
    } finally {
      setAriaKeySaving(false);
    }
  };

  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    const prev = document.title;
    document.title = "Stats — AIROBOTCAMSTUDIO";
    return () => {
      document.head.removeChild(meta);
      document.title = prev;
    };
  }, []);

  const load = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const [summaryRes, formsRes] = await Promise.all([
        axios.get(`${API}/analytics/summary`, { params: { key } }),
        axios.get(`${API}/analytics/forms`, { params: { key } }),
      ]);
      setStats(summaryRes.data);
      setForms(formsRes.data);
      if (!visitMarked.current) {
        visitMarked.current = true;
        try {
          setNewSince(window.localStorage.getItem("airobotcam_stats_last_visit"));
          window.localStorage.setItem("airobotcam_stats_last_visit", new Date().toISOString());
        } catch {
          setNewSince(null);
        }
      }
    } catch {
      setError("Invalid key.");
      setStats(null);
      setForms(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="stats-page" className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-[64px] max-w-5xl items-center justify-between px-6">
          <span className="font-display text-sm font-semibold tracking-[0.25em]">
            AIROBOTCAM<span className="text-cyan-300">STUDIO</span>
          </span>
          <span className="text-[11px] uppercase tracking-[0.25em] text-white/55">Private stats</span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-12 lg:py-16">
        {!stats ? (
          <form data-testid="stats-key-form" onSubmit={load} className="mx-auto mt-16 max-w-sm">
            <p className="font-display text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-300/90">
              Restricted
            </p>
            <h1 className="font-display mt-4 text-2xl font-light tracking-tighter">Website Statistics</h1>
            <p className="mt-3 text-sm text-white/50">Enter the analytics key to view statistics.</p>
            <input
              type="password"
              data-testid="stats-key-input"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Analytics key"
              className="mt-6 w-full rounded-md border border-white/12 bg-black/40 px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors focus:border-cyan-300/60"
            />
            {error && (
              <p data-testid="stats-error" role="alert" className="mt-3 text-sm text-red-300">
                {error}
              </p>
            )}
            <button
              data-testid="stats-submit"
              type="submit"
              disabled={loading}
              className="mt-5 w-full rounded-md bg-cyan-300 px-6 py-3 text-sm font-bold text-black transition-colors hover:bg-cyan-200 disabled:opacity-50"
            >
              {loading ? "Loading…" : "View stats"}
            </button>
          </form>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h1 className="font-display text-2xl font-light tracking-tighter">Website Statistics</h1>
              <button
                data-testid="stats-refresh"
                onClick={load}
                className="rounded-md border border-cyan-300/25 px-4 py-2 text-xs font-semibold text-cyan-200/80 transition-colors hover:border-cyan-300/50 hover:text-cyan-100"
              >
                Refresh
              </button>
            </div>

            <div data-testid="stats-cards" className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
              {[
                ["Page views", stats.human_page_views],
                ["Unique visitors", stats.unique_visitors],
                ["Search engine visits", stats.search_engine_visits],
                ["Bot hits (excluded)", stats.bot_hits],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">{label}</p>
                  <p className="font-display mt-3 text-4xl font-extralight tracking-tighter text-white">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <DailyChart data={stats.daily_views} />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
              <BarList title="Top pages" data={stats.top_pages} testid="stats-pages" />
              <BarList title="Key sections viewed" data={stats.sections_viewed} testid="stats-sections" />
              <BarList title="Referrers" data={stats.referrers} testid="stats-referrers" />
              <BarList
                title="Countries / Regions"
                data={Object.keys(stats.countries).some((c) => c !== "XX") ? stats.countries : stats.regions}
                testid="stats-geo"
              />
              <BarList title="Devices" data={stats.devices} testid="stats-devices" />
            </div>

            <div
            data-testid="aria-key-card"
            className="mb-12 rounded-2xl border border-cyan-300/20 bg-cyan-300/[0.03] p-6"
          >
            <p className="font-display text-[11px] font-semibold uppercase tracking-[0.25em] text-cyan-300/90">
              Aria Preview Key
            </p>
            <p className="mt-2 text-sm leading-relaxed text-[#C5CBD5]">
              Status:{" "}
              {stats.aria_configured ? (
                <span data-testid="aria-key-status" className="font-semibold text-cyan-300">Configured</span>
              ) : (
                <span data-testid="aria-key-status" className="font-semibold text-amber-300">Not configured</span>
              )}
            </p>
            <p className="mt-1.5 text-xs leading-relaxed text-white/45">
              Paste the LemonSlice API key here to connect it to the backend securely. It is stored
              in the server environment only — never shown again, never sent to the frontend.
            </p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <input
                type="password"
                data-testid="aria-key-input"
                value={ariaKeyInput}
                onChange={(e) => setAriaKeyInput(e.target.value)}
                placeholder="Paste LemonSlice API key"
                autoComplete="off"
                className="w-full flex-1 rounded-md border border-white/12 bg-black/40 px-4 py-2.5 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-cyan-300/60"
              />
              <button
                data-testid="aria-key-save"
                onClick={saveAriaKey}
                disabled={ariaKeySaving || ariaKeyInput.trim().length < 8}
                className="rounded-md bg-cyan-300 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.1em] text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {ariaKeySaving ? "Saving…" : "Save Key"}
              </button>
            </div>
            {ariaKeyMsg && (
              <p data-testid="aria-key-msg" className="mt-3 text-xs text-cyan-200/80">{ariaKeyMsg}</p>
            )}
          </div>

          {forms && <FormFeed forms={forms} newSince={newSince} dashKey={key} onChanged={refreshForms} />}

            <p className="mt-8 text-xs leading-relaxed text-white/50">
              Cookieless first-party analytics. Visitors are counted with an anonymous identifier
              that rotates daily; unique-visitor figures therefore reflect unique days, not
              individuals across days. Bot traffic is detected and excluded from all human figures.
            </p>
          </>
        )}
      </main>
    </div>
  );
}
